import React from "react";

import { faSpinnerThird } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { omit, throttle } from "lodash";
import { Button, Visibility } from "semantic-ui-react";

import { ScrollContext } from "~/layout/ScrollContext";

import styles from "./css/LoadMoreButton.module.css";

interface LoadMoreButtonProps {
	loadMore: (callback: () => void) => void;
	context?: object;
	onScreenTimeout: number;
	debounceTimeout: number;
	fireOnMount: boolean;
	continuous: boolean;
	autoLoad: boolean;
	disabled: boolean;
	wrapperStyle: object;
	loadingUp: boolean;
	content: string;
	dotted?: boolean;
}

interface LoadMoreButtonState {
	loading: boolean;
}

export default class LoadMoreButton extends React.Component<LoadMoreButtonProps, LoadMoreButtonState> {
	state: LoadMoreButtonState = { loading: false };
	mounted = false;

	componentDidMount() {
		this.mounted = true;
	}

	componentWillUnmount() {
		this.mounted = false;
	}

	static defaultProps = {
		onScreenTimeout: 100,
		debounceTimeout: 200,
		fireOnMount: true,
		continuous: false,
		autoLoad: true,
		disabled: false,
		wrapperStyle: {},
		loadingUp: false,
		content: "Load more",
		dotted: true,
	};

	onLoaded = (): void => {
		if (this.mounted) this.setState({ loading: false });
	};

	loadMore = (): void => {
		if (this.props.disabled) return;
		if (this.state.loading) return;
		this.setState({ loading: true }, this.props.loadMore.bind(this, this.onLoaded));
	};

	debouncedLoadMore: () => void = throttle(this.loadMore, this.props.debounceTimeout);

	onOnScreen = (): void => {
		if (!this.props.autoLoad || this.state.loading) return;
		setTimeout(this.debouncedLoadMore, this.props.onScreenTimeout);
	};

	render() {
		const buttonProps = omit(this.props, [
			"loadMore",
			"context",
			"onScreenTimeout",
			"debounceTimeout",
			"fireOnMount",
			"continuous",
			"autoLoad",
			"wrapperStyle",
			"disabled",
			"loadingUp",
			"content",
			"dotted",
		]);

		return (
			<ScrollContext.Consumer>
				{scrollEl => (
					<Visibility
						once={false}
						fireOnMount={this.props.fireOnMount}
						continuous={this.props.continuous}
						context={this.props.context || scrollEl || undefined}
						onOnScreen={this.onOnScreen}
						style={this.props.wrapperStyle}
						className={cx(styles.buttonWrapper, { [styles.dotted]: this.props.dotted })}
					>
						<Button
							basic
							circular
							compact
							content={this.state.loading ? <FontAwesomeIcon icon={faSpinnerThird} spin={true} /> : this.props.content}
							className={cx(styles.loadMoreButton, this.props.loadingUp ? styles.caretUp : styles.caretDown, {
								[styles.loading]: this.state.loading,
							})}
							onClick={this.debouncedLoadMore}
							{...buttonProps}
						/>
					</Visibility>
				)}
			</ScrollContext.Consumer>
		);
	}
}
